import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Button, Dialog, DialogContent, Grid, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

import { IProcurementMethod } from "models/ProcurementMethod/procurementMethodModel";
import { ProcurementMethodService } from "services/procurementMethod.service";
import { CustomProcurementMethodSelect } from "./CustomProcurementMethodSelect";
import CustomTextField from "components/CustomTextField";

type ProcurementMethodForm = {
	procurementMethodId: number | null;
	comment: string;
};

type Props = {
	open: boolean;
	selectedProcurementMethodId: number;
	totalAmount: number;
	handleCloseDialog: (model?: ProcurementMethodForm) => void;
};

const ProcurementMethodSelectionDialog: React.FC<Props> = ({
	open,
	selectedProcurementMethodId,
	totalAmount,
	handleCloseDialog,
}) => {
	const validationSchema = Yup.object().shape({
		comment: Yup.string()
			.nullable()
			.max(250, "Comment must be at most 250 characters"),
	});

	const methods = useForm<ProcurementMethodForm>({
		defaultValues: { procurementMethodId: null, comment: "" },
		mode: "onChange",
		resolver: yupResolver(validationSchema),
	});
	const [procurementMethodOptions, setProcurementMethodOptions] = useState<
		IProcurementMethod[]
	>([]);
	const [showConfirmationMessage, setShowConfirmationMessage] =
		useState<boolean>(false);

	useEffect(() => {
		const getProcurementMethodSelectionList = async () => {
			try {
				const result =
					await ProcurementMethodService.getProcurementMethodSelectionList(
						selectedProcurementMethodId,
						totalAmount
					);
				const tempList: IProcurementMethod[] = [];
				result.map((p) =>
					tempList.push({
						procurementMethodId: p.procurementMethodId,
						procurementMethodNumber: p.procurementMethodNumber,
						description: p.description,
						procurementMethodType: p.procurementMethodName,
						thresholdFrom: 0,
						thresholdTo: 0,
						itemAvailabilityId: 0,
						procurementMethodAbbreviation: p.procurementMethodAbbreviation,
						procurementMethodCategory: `${p.procurementMethodCategory} (${p.procurementMethodAbbreviation})`,
						validityPeriodInDays: p.validityPeriodInDays,
						isTransversalContract: false,
					})
				);
				setProcurementMethodOptions(tempList);
			} catch (error) {}
		};
		getProcurementMethodSelectionList();
	}, [selectedProcurementMethodId, totalAmount]);

	const procurementMethodId = methods.watch("procurementMethodId");
	const comment = methods.watch("comment");

	const changeProcurementMethod = async (formData: ProcurementMethodForm) => {
		if (!formData.procurementMethodId || !formData.comment) return;
		handleCloseDialog(formData);
	};

	return (
		<Dialog
			maxWidth="sm"
			open={open}
			disableEscapeKeyDown
			disableEnforceFocus
			scroll="paper"
			className="dialog-scroll"
		>
			<DialogContent
				sx={{ padding: "10px !important" }}
				className="dialog-scroll-content"
			>
				<Grid
					container
					padding={3}
					sx={{
						border: "1px solid #034f8b",
						borderRadius: 4,
						marginLeft: 0,
					}}
				>
					<Grid item xs={12} textAlign="center" sx={{ pb: 2 }}>
						<Typography
							variant="h3"
							sx={{
								color: "#034f8b !important",
								fontSize: "23px !important",
							}}
						>
							{showConfirmationMessage
								? "Cancel Confirmation"
								: "Procurement Method Specification"}
						</Typography>
					</Grid>
					<FormProvider {...methods}>
						{showConfirmationMessage ? (
							<Grid item xs={12} sx={{ pb: 2 }}>
								Are you sure you want to close this window? Press Cancel to
								proceed with cancellation or Press Discard to cancel this
								action.
							</Grid>
						) : (
							<>
								<Grid item xs={12} sx={{ pb: 2 }}>
									<CustomProcurementMethodSelect
										name="procurementMethodId"
										label="Procurement Method Selection"
										options={procurementMethodOptions}
									/>
								</Grid>
								<Grid item xs={12} sx={{ pb: 2 }}>
									<CustomTextField label="Comment" name="comment" rows={4} />
								</Grid>
							</>
						)}
						<Grid item xs={12} textAlign="center" sx={{ pt: 4 }}>
							{!showConfirmationMessage && (
								<>
									<Button
										variant="contained"
										sx={{ mr: 3 }}
										disabled={!procurementMethodId || !comment}
										onClick={methods.handleSubmit(changeProcurementMethod)}
									>
										Change
									</Button>
									<Button
										variant="outlined"
										onClick={() => setShowConfirmationMessage(true)}
									>
										Cancel
									</Button>
								</>
							)}
							{showConfirmationMessage && (
								<>
									<Button
										variant="contained"
										sx={{ mr: 3 }}
										onClick={() => {
											setShowConfirmationMessage(false);
											methods.reset();
										}}
									>
										Discard
									</Button>
									<Button
										variant="outlined"
										onClick={() => handleCloseDialog()}
									>
										Cancel
									</Button>
								</>
							)}
						</Grid>
					</FormProvider>
				</Grid>
			</DialogContent>
		</Dialog>
	);
};

export default ProcurementMethodSelectionDialog;
